import * as React from "react";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";
import styled from "styled-components";

import { useEventListener } from "src/library/hooks/useEventListener";
import {
  ActionsPopOut,
  IStep
} from "src/library/components/ActionsPopOut/index";
import {
  Button,
  ButtonType
} from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import tokenizeString from "src/library/utils/string-manipulations/tokenize-string";
import { IStore } from "src/core/shared/store";
import { IDrop } from "src/core/drops/api/drops.api";
import { useCurrentUser } from "src/core/users/hooks";
import { UserOrderableDropSelect } from "src/core/drops/components/UserOrderableDropMultiSelect";

interface IShutdownContextProps {
  createShutdown: (dropOverride: IDrop | null) => void;
  drop: IDrop | null;
  $buttonType?: ButtonType;
}

/**
 * Creates shutdown context - provides requirement prompts if needed to create a shutdown
 */
export const CreateShutdownContext: React.ReactFCWithChildren<IShutdownContextProps> = ({
  createShutdown,
  drop,
  $buttonType = "primary"
}) => {
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings?.terminology }));

  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  const divRef = React.useRef<HTMLDivElement>(null);
  const [ showAction, setShowAction ] = React.useState(false);
  const [ { currentRole } ] = useCurrentUser();
  const isCustomerAdmin = React.useMemo(() => currentRole?.code === "customer-admin", [ currentRole?.code ]);
  const [ selectedDrop, setSelectedDrop ] = React.useState<IDrop | null>(drop);

  const toggleShowAction = () => {
    // Open up action dropdown for user:
    setShowAction(!showAction);
  };

  React.useEffect(() => {
    if (showAction) {
      setOverlayPosition(true);
    }
  }, [ showAction ]);

  const setOverlayPosition = (autoScroll = false) => {
    if (divRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;

      if (window.outerWidth <= 500) {
        right = 0;
      } else if (right + 400 > window.outerWidth) {
        right = bounds.right - 300;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });

      if (autoScroll) {
        const scrollWrapper = document.getElementById("main-wrapper");

        if (scrollWrapper) {
          const currentBottom = bounds.top + 350;
          const currentScroll = scrollWrapper.scrollTop;

          if (currentBottom > window.outerHeight) {
            scrollWrapper.scrollTo({
              top: currentScroll + (currentBottom - window.outerHeight),
              behavior: "smooth"
            });
          }
        }
      }
    }
  };

  const dropSelectionStep = React.useMemo(() => {
    if (isCustomerAdmin && !drop) {
      const dropSelectionStep: IStep = {
        title: `Select ${terminology?.drop.singular.text}`,
        allowNext: !!selectedDrop,
        hideFooter: false,
        component: (
          <SelectDropWrapper>
            <UserOrderableDropSelect
              onDropsChange={seldrop => setSelectedDrop(seldrop)}
              selectedDrop={selectedDrop}
            />
          </SelectDropWrapper>
        )
      };

      return dropSelectionStep;
    }

    return null;
  }, [
    drop,
    isCustomerAdmin,
    selectedDrop,
    terminology?.drop.singular.text
  ]);

  const createListSteps = React.useMemo(() => {
    const steps: IStep[] = [];

    if (dropSelectionStep) {
      steps.unshift(dropSelectionStep);
    }

    return steps;
  }, [ dropSelectionStep ]);

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, []);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));

  return (
    <>
      <div style={{ position: "relative" }}>
        <Button
          icon={Icons.HOLIDAY}
          $buttonType={$buttonType}
          onClick={!selectedDrop ? toggleShowAction : () => createShutdown(selectedDrop)}
          size="medium"
        >
          {`Create a new ${terminology ? tokenizeString("%SHUTDOWN%", terminology) : "shutdown"}`}
        </Button>

        {showAction && (
          <ActionsPopOut
            {...position}
            action={() => createShutdown(selectedDrop)}
            handleClose={() => {
              toggleShowAction();
            }}
            directionHorizontal="left"
            directionVertical="top"
            steps={createListSteps}
          />
        )}
      </div>
    </>
  );
};

const SelectDropWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.padding.md};
  flex-direction: column;
  padding-top: 0;
  > * {
    margin-top: ${({ theme }) => theme.spacing.padding.sm};
  }
`;
